/**
 * L_INCORPORATIONS.INCORPORATION_DATA → SG Incorporation (Pre) live document values.
 */
import {
  buildSgDocumentVariables,
  extractSgFormFromIncorporationData,
} from '@/features/setupEntity/utils/sgIncorporationPayload';
import type { SgIncorporationForm } from '@/features/setupEntity/components/sg/types/sgIncorporation.types';
import { nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';

function str(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v) || (typeof v === 'object' && v !== null)) return '';
  return String(v).trim();
}

function b04Label(v: unknown): string {
  const s = str(v);
  if (!s) return '';
  return nationalityLabelForOutput(s) || s;
}

function flattenScalars(obj: Record<string, unknown>, out: Record<string, string>) {
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    if (Array.isArray(v) || (typeof v === 'object' && v !== null)) continue;
    // service checkboxes: false → 'no' (Studio % / Word if not 용)
    if (/^srv_a_\d+$/.test(k) && v === false) {
      out[k] = 'no';
      continue;
    }
    const s = str(v);
    if (s) out[k] = s;
  }
}

/** Setup Entity Yes/No → Pre 문서·Stage 포함 여부 */
export interface SgIncPreIncludes {
  directors: boolean;
  secretary: boolean;
  shareholders: boolean;
  controllers: boolean;
  engagement: boolean;
}

const ALL_INCLUDED: SgIncPreIncludes = {
  directors: true,
  secretary: true,
  shareholders: true,
  controllers: true,
  engagement: true,
};

function isYes(v: unknown): boolean {
  return String(v ?? '')
    .trim()
    .toLowerCase() === 'yes';
}

export interface SgIncPreMapped {
  values: Record<string, string>;
  form45Ids: number[];
  form45bIds: number[];
  corpCtrlIds: number[];
  indCtrlIds: number[];
  csProfileIds: number[];
  includes: SgIncPreIncludes;
  /** Client Acceptance 반복 행 id */
  caf: {
    directors: number[];
    shareholders: number[];
    ubos: number[];
    peps: number[];
    mds: number[];
    ceos: number[];
    partners: number[];
    others: number[];
    secretaries: number[];
    indControllers: number[];
    corpControllers: number[];
  };
}

/**
 * incorporation entity-data / INCORPORATION_DATA → DocScope 키 + CAF 행 상태.
 */
export function mapSgIncorporationToPreValues(
  incorporationData: Record<string, any> | null | undefined,
): SgIncPreMapped {
  const values: Record<string, string> = {};
  const empty: SgIncPreMapped = {
    values,
    form45Ids: [1],
    form45bIds: [1],
    corpCtrlIds: [1],
    indCtrlIds: [1],
    csProfileIds: [1],
    includes: { ...ALL_INCLUDED },
    caf: {
      directors: [1],
      shareholders: [1],
      ubos: [1],
      peps: [1],
      mds: [1],
      ceos: [1],
      partners: [1],
      others: [1],
      secretaries: [1],
      indControllers: [1],
      corpControllers: [1],
    },
  };

  if (!incorporationData || typeof incorporationData !== 'object') return empty;

  let form: SgIncorporationForm | null = null;
  if (incorporationData.sgIncorporationForm) {
    form = incorporationData.sgIncorporationForm as SgIncorporationForm;
  } else {
    form = extractSgFormFromIncorporationData(incorporationData);
  }
  if (!form) {
    // documentVariables 만 있는 경우
    const dv = (incorporationData.documentVariables || {}) as Record<string, unknown>;
    flattenScalars(dv, values);
    if (incorporationData.companyName) values.company_name = String(incorporationData.companyName);

    const dirs: any[] = Array.isArray(dv.directors) ? (dv.directors as any[]) : [];
    const corps: any[] = Array.isArray(dv.corporate_controllers)
      ? (dv.corporate_controllers as any[])
      : [];
    const inds: any[] = Array.isArray(dv.individual_controllers)
      ? (dv.individual_controllers as any[])
      : [];
    const shAll: any[] = Array.isArray(dv.shareholders) ? (dv.shareholders as any[]) : [];
    const corpSh = shAll.filter((s) =>
      String(s.shareholder_type || s.shareholderType || '').toLowerCase().includes('corp'),
    );

    const rawInc = incorporationData as Record<string, unknown>;
    const includes: SgIncPreIncludes = {
      directors:
        'includeDirectors' in rawInc ? isYes(rawInc.includeDirectors) : dirs.length > 0,
      secretary:
        'includeSecretary' in rawInc
          ? isYes(rawInc.includeSecretary)
          : !!(dv.secretary_name || (dv.secretary as any)?.name),
      shareholders:
        'includeShareholders' in rawInc ? isYes(rawInc.includeShareholders) : shAll.length > 0,
      controllers:
        'includeControllers' in rawInc
          ? isYes(rawInc.includeControllers)
          : corps.length > 0 || inds.length > 0,
      engagement:
        'includeEngagement' in rawInc
          ? isYes(rawInc.includeEngagement)
          : !!(dv.csp_name || dv.engagement_letter_date),
    };

    const form45Ids = includes.directors
      ? dirs.length
        ? dirs.map((_, i) => i + 1)
        : [1]
      : [];
    if (includes.directors) {
      dirs.forEach((d, i) => {
        const sc = `f45_${i + 1}/`;
        const put = (k: string, v: unknown) => {
          const s = str(v);
          if (s) values[sc + k] = s;
        };
        put('company_name', values.company_name || dv.company_name);
        put('company_registration_no', values.company_registration_no || dv.company_registration_no);
        put('director_name', d.director_name || d.name);
        put('director_identification_no', d.director_identification_no || d.identification_no);
        put('director_nationality', d.director_nationality || d.nationality);
        put('director_address', d.director_address || d.address);
        put('director_alternate_address', d.director_alternate_address);
        put('appointment_effective_date', d.appointment_effective_date);
        put('execution_day', d.execution_day);
        put('execution_month', d.execution_month);
        put('execution_year', d.execution_year);
      });
    }

    const form45bIds = includes.secretary ? [1] : [];
    if (includes.secretary) {
      const sc = 'f45b_1/';
      const put = (k: string, v: unknown) => {
        const s = str(v);
        if (s) values[sc + k] = s;
      };
      const secObj = (dv.secretary && typeof dv.secretary === 'object' ? dv.secretary : {}) as Record<
        string,
        unknown
      >;
      put('company_name', values.company_name || dv.company_name);
      put('company_registration_no', values.company_registration_no || dv.company_registration_no);
      put('secretary_name', dv.secretary_name || secObj.name || secObj.secretary_name);
      put('secretary_address', dv.secretary_address || secObj.address || secObj.secretary_address);
      put(
        'secretary_identification_no',
        dv.secretary_identification_no || secObj.identification_no || secObj.secretary_identification_no,
      );
      put(
        'secretary_nationality',
        b04Label(
          dv.secretary_nationality || secObj.nationality || secObj.secretary_nationality,
        ),
      );
      // CAF Section A2b
      const putCaf = (suffix: string, v: unknown) => {
        const s = str(v);
        if (s) values[`sec1_${suffix}`] = s;
      };
      putCaf('secretary_name', values[`${sc}secretary_name`] || dv.secretary_name || secObj.name);
      putCaf(
        'secretary_identification_no',
        values[`${sc}secretary_identification_no`] || dv.secretary_identification_no,
      );
      putCaf(
        'secretary_nationality',
        values[`${sc}secretary_nationality`] ||
          b04Label(dv.secretary_nationality || secObj.nationality),
      );
      putCaf('secretary_address', values[`${sc}secretary_address`] || dv.secretary_address);
    }

    const corpCtrlIds = includes.controllers
      ? corps.length
        ? corps.map((_, i) => i + 1)
        : [1]
      : [];
    if (includes.controllers) {
      corps.forEach((c, i) => {
        const sc = `ctrlc_${i + 1}/`;
        Object.entries(c).forEach(([k, v]) => {
          const s = str(v);
          if (s) values[sc + k] = s;
        });
        if (values.company_name) values[sc + 'company_name'] = values.company_name;
        // CAF Corporate Controller
        const id = i + 1;
        const putCaf = (suffix: string, v: unknown) => {
          const s = str(v);
          if (s) values[`cctrl${id}_${suffix}`] = s;
        };
        putCaf(
          'controller_corporate_name',
          c.controller_corporate_name || c.name || c.corporate_name,
        );
        putCaf('controller_uen', c.controller_uen || c.uen || c.registration_no);
        putCaf(
          'controller_registered_office',
          c.controller_registered_office || c.registered_office || c.address,
        );
        putCaf('controller_email', c.controller_email || c.email);
        putCaf('controller_contact_no', c.controller_contact_no || c.contact_no || c.phone);
      });
    }

    const indCtrlIds = includes.controllers
      ? inds.length
        ? inds.map((_, i) => i + 1)
        : [1]
      : [];
    if (includes.controllers) {
      inds.forEach((c, i) => {
        const sc = `ctrli_${i + 1}/`;
        Object.entries(c).forEach(([k, v]) => {
          const s = str(v);
          if (s) values[sc + k] = s;
        });
        if (values.company_name) values[sc + 'company_name'] = values.company_name;
        // CAF Individual Controller
        const id = i + 1;
        const putCaf = (suffix: string, v: unknown) => {
          const s = str(v);
          if (s) values[`ictrl${id}_${suffix}`] = s;
        };
        putCaf('controller_full_name', c.controller_full_name || c.name || c.full_name);
        putCaf(
          'controller_identification_no',
          c.controller_identification_no || c.identification_no,
        );
        putCaf(
          'controller_nationality',
          b04Label(c.controller_nationality || c.nationality),
        );
        putCaf(
          'controller_date_of_birth',
          c.controller_date_of_birth || c.date_of_birth || c.dob,
        );
        putCaf(
          'controller_residential_address',
          c.controller_residential_address || c.residential_address || c.address,
        );
        putCaf('controller_email', c.controller_email || c.email);
        putCaf('controller_contact_no', c.controller_contact_no || c.contact_no || c.phone);
      });
    }

    const csProfileIds = includes.shareholders
      ? corpSh.length
        ? corpSh.map((_, i) => i + 1)
        : [1]
      : [];

    const indSh = shAll.filter(
      (s) => !String(s.shareholder_type || s.shareholderType || '').toLowerCase().includes('corp'),
    );
    const cafSh = indSh.length ? indSh.map((_, i) => i + 1) : [1];
    indSh.forEach((s, i) => {
      const id = i + 1;
      const put = (suffix: string, v: unknown) => {
        const sv = str(v);
        if (sv) values[`sh${id}_${suffix}`] = sv;
      };
      put('shareholder_name', s.shareholder_name || s.name);
      put('shareholder_identification_no', s.shareholder_identification_no || s.identification_no);
      put('shareholder_nationality', b04Label(s.shareholder_nationality || s.nationality));
      put('shareholder_date_of_birth', s.shareholder_date_of_birth || s.date_of_birth);
      put('shareholder_address', s.shareholder_address || s.address);
      put('shareholder_email', s.shareholder_email || s.email);
      put('shareholder_mobile_no', s.shareholder_mobile_no || s.mobile_no);
      put('shareholder_number_of_shares', s.shareholder_number_of_shares || s.number_of_shares || s.no_of_shares);
    });

    dirs.forEach((d, i) => {
      const id = i + 1;
      const put = (suffix: string, v: unknown) => {
        const sv = str(v);
        if (sv) values[`dir${id}_${suffix}`] = sv;
      };
      put('director_name', d.director_name || d.name);
      put('director_identification_no', d.director_identification_no || d.identification_no);
      put('director_nationality', b04Label(d.director_nationality || d.nationality));
      put('director_date_of_birth', d.director_date_of_birth || d.date_of_birth);
      put('director_address', d.director_address || d.address);
      put('director_email', d.director_email || d.email);
      put('director_mobile_no', d.director_mobile_no || d.mobile_no);
      put('director_tel_no', d.director_tel_no || d.tel_no);
    });

    const pepsDv: any[] = Array.isArray(dv.peps) ? (dv.peps as any[]) : [];
    const cafPep = pepsDv.length ? pepsDv.map((_, i) => i + 1) : [1];
    pepsDv.forEach((p, i) => {
      const id = i + 1;
      const put = (suffix: string, v: unknown) => {
        const sv = str(v);
        if (sv) values[`pep${id}_${suffix}`] = sv;
      };
      put('pep_name', p.name);
      put('pep_country', b04Label(p.country));
      put('pep_function', p.function);
      put('pep_period', p.period);
      put('pep_relationship', p.relationship);
      put('pep_category', p.category);
    });
    if (pepsDv.length) values.has_pep = 'yes';

    const ubosDv: any[] = Array.isArray(dv.ubos) ? (dv.ubos as any[]) : [];
    const cafUbo = ubosDv.length ? ubosDv.map((_, i) => i + 1) : [1];

    const cafSec = includes.secretary ? [1] : [1];
    const cafIndCtrl = indCtrlIds.length ? indCtrlIds : [1];
    const cafCorpCtrl = corpCtrlIds.length ? corpCtrlIds : [1];

    return {
      ...empty,
      values,
      form45Ids,
      form45bIds,
      corpCtrlIds,
      indCtrlIds,
      csProfileIds,
      includes,
      caf: {
        ...empty.caf,
        directors: form45Ids.length ? form45Ids : [1],
        shareholders: cafSh,
        ubos: cafUbo,
        peps: cafPep,
        secretaries: cafSec,
        indControllers: cafIndCtrl,
        corpControllers: cafCorpCtrl,
      },
    };
  }

  const includes: SgIncPreIncludes = {
    directors: isYes(form.includeDirectors),
    secretary: isYes(form.includeSecretary),
    shareholders: isYes(form.includeShareholders),
    controllers: isYes(form.includeControllers),
    engagement: isYes(form.includeEngagement),
  };

  const vars = buildSgDocumentVariables(form) as Record<string, any>;
  flattenScalars(vars, values);

  // ── Form 45 — director 별 DocScope ──
  const directors: any[] = Array.isArray(vars.directors) ? vars.directors : [];
  const form45Ids = includes.directors
    ? directors.length
      ? directors.map((_, i) => i + 1)
      : [1]
    : [];
  if (includes.directors) {
    directors.forEach((d, i) => {
      const sc = `f45_${i + 1}/`;
      const put = (k: string, v: unknown) => {
        const s = str(v);
        if (s) values[sc + k] = s;
      };
      put('company_name', vars.company_name);
      put('company_registration_no', vars.company_registration_no);
      put('director_name', d.director_name || d.name);
      put('director_identification_no', d.director_identification_no);
      put('director_nationality', b04Label(d.director_nationality));
      put('director_address', d.director_address);
      put('director_alternate_address', d.director_alternate_address);
      put('appointment_effective_date', d.appointment_effective_date);
      put('execution_day', d.execution_day);
      put('execution_month', d.execution_month);
      put('execution_year', d.execution_year);
    });
  }

  // ── Form 45B ──
  const form45bIds = includes.secretary ? [1] : [];
  if (includes.secretary) {
    const sc = 'f45b_1/';
    const put = (k: string, v: unknown) => {
      const s = str(v);
      if (s) values[sc + k] = s;
    };
    put('company_name', vars.company_name);
    put('company_registration_no', vars.company_registration_no);
    put('secretary_name', vars.secretary_name);
    put('secretary_address', vars.secretary_address);
    put('secretary_identification_no', vars.secretary_identification_no);
    put('secretary_nationality', b04Label(vars.secretary_nationality));
    put('appointment_effective_date', vars.appointment_effective_date);
    put('execution_date', vars.execution_date);
  }

  // ── Corporate controllers ──
  const corps: any[] = Array.isArray(vars.corporate_controllers) ? vars.corporate_controllers : [];
  const corpCtrlIds = includes.controllers
    ? corps.length
      ? corps.map((_, i) => i + 1)
      : [1]
    : [];
  const noticeFallbackKeys = [
    'notice_date',
    'notice_recipient_name',
    'reply_to_address',
    'notice_signer_name',
    'company_name',
  ] as const;
  const applyNoticeFallback = (sc: string) => {
    for (const k of noticeFallbackKeys) {
      if (values[sc + k]) continue;
      const s = str(vars[k]);
      if (s) values[sc + k] = s;
    }
  };
  if (includes.controllers) {
    if (corps.length) {
      corps.forEach((c, i) => {
        const sc = `ctrlc_${i + 1}/`;
        Object.entries(c).forEach(([k, v]) => {
          const s = str(v);
          if (s) values[sc + k] = s;
        });
        applyNoticeFallback(sc);
      });
    } else {
      applyNoticeFallback('ctrlc_1/');
    }
  }

  // ── Individual controllers ──
  const inds: any[] = Array.isArray(vars.individual_controllers) ? vars.individual_controllers : [];
  const indCtrlIds = includes.controllers
    ? inds.length
      ? inds.map((_, i) => i + 1)
      : [1]
    : [];
  if (includes.controllers) {
    if (inds.length) {
      inds.forEach((c, i) => {
        const sc = `ctrli_${i + 1}/`;
        Object.entries(c).forEach(([k, v]) => {
          const s = str(v);
          if (s) values[sc + k] = s;
        });
        applyNoticeFallback(sc);
      });
    } else {
      applyNoticeFallback('ctrli_1/');
    }
  }

  // ── Corporate shareholder profiles (from corporate shareholders) ──
  const shAll: any[] = Array.isArray(vars.shareholders) ? vars.shareholders : [];
  const corpSh = shAll.filter(
    (s) => String(s.shareholder_type || s.shareholderType || '').toLowerCase().includes('corp'),
  );
  const csProfileIds = includes.shareholders
    ? corpSh.length
      ? corpSh.map((_, i) => i + 1)
      : [1]
    : [];
  if (includes.shareholders) corpSh.forEach((s, i) => {
    const sc = `cs_${i + 1}/`;
    const put = (k: string, v: unknown) => {
      const sVal = str(v);
      if (sVal) values[sc + k] = sVal;
    };
    put('cs_company_name', s.cs_company_name || s.name);
    put('cs_registration_no', s.cs_registration_no || s.identification_no);
    put('cs_former_name', s.cs_former_name);
    put('cs_country_of_incorporation', s.cs_country_of_incorporation);
    put('cs_incorporation_date', s.cs_incorporation_date);
    put('cs_business_activity', s.cs_business_activity);
    put('cs_paid_up_capital', s.cs_paid_up_capital);
    put('cs_no_of_shares_issued', s.cs_no_of_shares_issued);
    put('cs_registered_address', s.cs_registered_address || s.address);
    put('cs_last_financial_year', s.cs_last_financial_year);
    put('cs_website', s.cs_website);
    put(
      'cs_is_public_listed',
      s.cs_is_public_listed === true || s.cs_is_public_listed === 'yes'
        ? 'Yes'
        : s.cs_is_public_listed === false || s.cs_is_public_listed === 'no'
          ? 'No'
          : s.cs_is_public_listed,
    );
    put('cs_contact_person', s.cs_contact_person);
    put('cs_contact_no_office', s.cs_contact_no_office);
    put('cs_email', s.cs_email);
    put('cs_mobile_no', s.cs_mobile_no);

    const csDirs: any[] = Array.isArray(s.cs_directors) ? s.cs_directors : [];
    csDirs.forEach((d, di) => {
      const id = di + 1;
      put(`d${id}_no`, d.no ?? id);
      put(`d${id}_name`, d.name);
      put(`d${id}_nationality`, d.nationality);
      put(`d${id}_identification_no`, d.identification_no);
      put(`d${id}_residential_address`, d.residential_address);
      put(`d${id}_date_of_appointment`, d.date_of_appointment);
    });

    const csShs: any[] = Array.isArray(s.cs_shareholders) ? s.cs_shareholders : [];
    csShs.forEach((sh, si) => {
      const id = si + 1;
      put(`s${id}_no`, sh.no ?? id);
      put(`s${id}_name`, sh.name);
      put(`s${id}_nationality`, sh.nationality);
      put(`s${id}_identification_no`, sh.identification_no);
      put(`s${id}_residential_address`, sh.residential_address);
      put(`s${id}_date_of_appointment`, sh.date_of_appointment);
      put(`s${id}_no_of_shares_and_percentage`, sh.no_of_shares_and_percentage);
    });
  });

  // ── Client Acceptance 반복 행 ──
  const cafDirs = directors.length ? directors.map((_, i) => i + 1) : [1];
  directors.forEach((d, i) => {
    const id = i + 1;
    const put = (suffix: string, v: unknown) => {
      const s = str(v);
      if (s) values[`dir${id}_${suffix}`] = s;
    };
    put('director_name', d.director_name || d.name);
    put('director_identification_no', d.director_identification_no);
    put('director_nationality', b04Label(d.director_nationality));
    put('director_date_of_birth', d.director_date_of_birth);
    put('director_address', d.director_address);
    put('director_email', d.director_email);
    put('director_mobile_no', d.director_mobile_no);
    put('director_tel_no', d.director_tel_no);
    put('is_pep', d.is_pep === true || d.is_pep === 'yes' ? 'Yes' : d.is_pep === false ? 'No' : str(d.is_pep));
  });

  // CAF Section A2b — Secretary (Form 45B 와 별도 키)
  const cafSec = includes.secretary ? [1] : [1];
  if (includes.secretary) {
    const put = (suffix: string, v: unknown) => {
      const s = str(v);
      if (s) values[`sec1_${suffix}`] = s;
    };
    put('secretary_name', vars.secretary_name);
    put('secretary_identification_no', vars.secretary_identification_no);
    put('secretary_nationality', b04Label(vars.secretary_nationality));
    put('secretary_address', vars.secretary_address);
  }

  // CAF Individual Controllers
  const cafIndCtrl = includes.controllers
    ? inds.length
      ? inds.map((_, i) => i + 1)
      : [1]
    : [1];
  if (includes.controllers && inds.length) {
    inds.forEach((c, i) => {
      const id = i + 1;
      const put = (suffix: string, v: unknown) => {
        const s = str(v);
        if (s) values[`ictrl${id}_${suffix}`] = s;
      };
      put('controller_full_name', c.controller_full_name || c.name || c.full_name);
      put(
        'controller_identification_no',
        c.controller_identification_no || c.identification_no,
      );
      put('controller_nationality', b04Label(c.controller_nationality || c.nationality));
      put('controller_date_of_birth', c.controller_date_of_birth || c.date_of_birth);
      put(
        'controller_residential_address',
        c.controller_residential_address || c.residential_address || c.address,
      );
      put('controller_email', c.controller_email || c.email);
      put('controller_contact_no', c.controller_contact_no || c.contact_no);
    });
  }

  // CAF Corporate Controllers
  const cafCorpCtrl = includes.controllers
    ? corps.length
      ? corps.map((_, i) => i + 1)
      : [1]
    : [1];
  if (includes.controllers && corps.length) {
    corps.forEach((c, i) => {
      const id = i + 1;
      const put = (suffix: string, v: unknown) => {
        const s = str(v);
        if (s) values[`cctrl${id}_${suffix}`] = s;
      };
      put(
        'controller_corporate_name',
        c.controller_corporate_name || c.name || c.corporate_name,
      );
      put('controller_uen', c.controller_uen || c.uen || c.registration_no);
      put(
        'controller_registered_office',
        c.controller_registered_office || c.registered_office || c.address,
      );
      put('controller_email', c.controller_email || c.email);
      put('controller_contact_no', c.controller_contact_no || c.contact_no);
    });
  }

  const indSh = shAll.filter(
    (s) => !String(s.shareholder_type || s.shareholderType || '').toLowerCase().includes('corp'),
  );
  const cafSh = indSh.length ? indSh.map((_, i) => i + 1) : [1];
  indSh.forEach((s, i) => {
    const id = i + 1;
    const put = (suffix: string, v: unknown) => {
      const sv = str(v);
      if (sv) values[`sh${id}_${suffix}`] = sv;
    };
    put('shareholder_name', s.shareholder_name || s.name);
    put('shareholder_identification_no', s.shareholder_identification_no || s.identification_no);
    put('shareholder_nationality', b04Label(s.shareholder_nationality || s.nationality));
    put('shareholder_date_of_birth', s.shareholder_date_of_birth || s.date_of_birth);
    put('shareholder_address', s.shareholder_address || s.address);
    put('shareholder_email', s.shareholder_email || s.email);
    put('shareholder_mobile_no', s.shareholder_mobile_no || s.mobile_no);
    put('shareholder_number_of_shares', s.shareholder_number_of_shares || s.number_of_shares || s.no_of_shares);
    put(
      'is_pep',
      s.is_pep === true || s.is_pep === 'yes' ? 'Yes' : s.is_pep === false || s.is_pep === 'no' ? 'No' : str(s.is_pep),
    );
  });

  const ubos: any[] = Array.isArray(vars.ubos) ? vars.ubos : [];
  const cafUbo = ubos.length ? ubos.map((_, i) => i + 1) : [1];
  ubos.forEach((u, i) => {
    const id = i + 1;
    const put = (suffix: string, v: unknown) => {
      const sv = str(v);
      if (sv) values[`ubo${id}_${suffix}`] = sv;
    };
    put('ubo_name', u.name || u.ubo_name);
    put('ubo_identification_no', u.identification_no || u.ubo_identification_no);
    put('ubo_ownership_structure', u.ownership_structure || u.ubo_ownership_structure);
    put('ubo_residential_address', u.residential_address || u.ubo_residential_address);
    put('ubo_nationality', u.nationality || u.ubo_nationality);
    put('ubo_date_of_birth', u.date_of_birth || u.ubo_date_of_birth);
    put('ubo_contact_no', u.contact_no || u.ubo_contact_no);
    put('ubo_email', u.email || u.ubo_email);
    put(
      'is_pep',
      u.is_pep === true || u.is_pep === 'yes' ? 'Yes' : u.is_pep === false || u.is_pep === 'no' ? 'No' : str(u.is_pep),
    );
  });

  const peps: any[] = Array.isArray(vars.peps) ? vars.peps : [];
  const cafPep = peps.length ? peps.map((_, i) => i + 1) : [1];
  peps.forEach((p, i) => {
    const id = i + 1;
    const put = (suffix: string, v: unknown) => {
      const sv = str(v);
      if (sv) values[`pep${id}_${suffix}`] = sv;
    };
    put('pep_name', p.name);
    put('pep_country', p.country);
    put('pep_function', p.function);
    put('pep_period', p.period);
    put('pep_relationship', p.relationship);
    put('pep_category', p.category);
  });

  const mds: any[] = Array.isArray(vars.managing_directors) ? vars.managing_directors : [];
  const cafMd = mds.length ? mds.map((_, i) => i + 1) : [1];
  mds.forEach((r, i) => {
    if (r.name) values[`md${i + 1}_md_name`] = String(r.name);
  });

  const ceos: any[] = Array.isArray(vars.ceos) ? vars.ceos : [];
  const cafCeo = ceos.length ? ceos.map((_, i) => i + 1) : [1];
  ceos.forEach((r, i) => {
    if (r.name || r.ceo_name) values[`ceo${i + 1}_ceo_name`] = String(r.ceo_name || r.name);
  });

  const partners: any[] = Array.isArray(vars.partners) ? vars.partners : [];
  const cafPa = partners.length ? partners.map((_, i) => i + 1) : [1];
  partners.forEach((r, i) => {
    if (r.name || r.p_name) values[`pa${i + 1}_p_name`] = String(r.p_name || r.name);
  });

  const others: any[] = Array.isArray(vars.others) ? vars.others : [];
  const cafOt = others.length ? others.map((_, i) => i + 1) : [1];
  others.forEach((r, i) => {
    if (r.name || r.o_name) values[`ot${i + 1}_o_name`] = String(r.o_name || r.name);
    if (r.o_role || r.role) values[`ot${i + 1}_o_role`] = String(r.o_role || r.role);
  });

  // Yes/No checkboxes stored as strings
  if (vars.has_corporate_shareholder != null) {
    values.has_corporate_shareholder =
      vars.has_corporate_shareholder === true || vars.has_corporate_shareholder === 'yes'
        ? 'yes'
        : vars.has_corporate_shareholder === false || vars.has_corporate_shareholder === 'no'
          ? 'no'
          : str(vars.has_corporate_shareholder);
  }
  if (vars.has_pep != null) {
    values.has_pep =
      vars.has_pep === true || vars.has_pep === 'yes'
        ? 'yes'
        : vars.has_pep === false || vars.has_pep === 'no'
          ? 'no'
          : str(vars.has_pep);
  } else if (peps.length) {
    values.has_pep = 'yes';
  }
  if (vars.ihc_is_public != null) {
    values.ihc_is_public =
      vars.ihc_is_public === true || vars.ihc_is_public === 'yes' ? 'yes' : 'no';
  }
  if (vars.upc_is_public != null) {
    values.upc_is_public =
      vars.upc_is_public === true || vars.upc_is_public === 'yes' ? 'yes' : 'no';
  }

  // Engagement fee alias (wizard stores fee_registered_address)
  if (!values.fee_annual_registered_address && values.fee_registered_address) {
    values.fee_annual_registered_address = values.fee_registered_address;
  }

  // ACRA Signature — 미입력이면 officer 성명과 동일하게 (Name 필드와 같은 텍스트 표시)
  if (!values.recommending_officer_signature && values.recommending_officer_name) {
    values.recommending_officer_signature = values.recommending_officer_name;
  }
  if (!values.approving_officer_signature && values.approving_officer_name) {
    values.approving_officer_signature = values.approving_officer_name;
  }

  // 행별 통화 — Setup 의 currency_symbol 로 초기값 채움 (이후 행마다 독립 편집)
  const defaultCur = values.currency_symbol || 'SGD';
  const feeCurrencyKeys = [
    'currency_fee_incorporation_package',
    'currency_fee_nominee_director',
    'currency_fee_nominee_director_deposit',
    'currency_fee_employment_pass',
    'currency_fee_corppass_opening',
    'currency_fee_bank_account_opening',
    'currency_fee_annual_secretarial',
    'currency_fee_annual_registered_address',
    'currency_fee_annual_nominee_director',
    'currency_fee_annual_xbrl',
    'currency_fee_annual_corppass_maintenance',
  ];
  for (const k of feeCurrencyKeys) {
    if (!values[k]) values[k] = defaultCur;
  }

  return {
    values,
    form45Ids,
    form45bIds,
    corpCtrlIds,
    indCtrlIds,
    csProfileIds,
    includes,
    caf: {
      directors: cafDirs,
      shareholders: cafSh,
      ubos: cafUbo,
      peps: cafPep,
      mds: cafMd,
      ceos: cafCeo,
      partners: cafPa,
      others: cafOt,
      secretaries: cafSec,
      indControllers: cafIndCtrl,
      corpControllers: cafCorpCtrl,
    },
  };
}
